import { createContext, useContext, useMemo, useState } from "react";

const SHELL_CACHE_STORAGE_KEY = "dimerse-shell-cache-v1";
const SHELL_CACHE_TTL_MS = 5 * 60 * 1000;

const ShellCacheContext = createContext(null);

const loadInitialCache = () => {
  try {
    const raw = localStorage.getItem(SHELL_CACHE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return typeof parsed === "object" && parsed ? parsed : {};
  } catch {
    return {};
  }
};

export const ShellCacheProvider = ({ children }) => {
  const [cache, setCache] = useState(loadInitialCache);

  const persist = (nextCache) => {
    setCache(nextCache);
    localStorage.setItem(SHELL_CACHE_STORAGE_KEY, JSON.stringify(nextCache));
  };

  const getEntry = (bucket, userKey) => {
    const entry = cache?.[bucket]?.[userKey];
    if (!entry) return null;
    if (!entry.updatedAt || Date.now() - entry.updatedAt > SHELL_CACHE_TTL_MS) return null;
    return entry.data;
  };

  const setEntry = (bucket, userKey, data) => {
    const nextCache = {
      ...cache,
      [bucket]: {
        ...(cache?.[bucket] || {}),
        [userKey]: {
          data,
          updatedAt: Date.now(),
        },
      },
    };
    persist(nextCache);
  };

  const value = useMemo(
    () => ({
      getSidebarMeta: (userKey) => getEntry("sidebarMeta", userKey),
      setSidebarMeta: (userKey, data) => setEntry("sidebarMeta", userKey, data),
      getNotifications: (userKey) => getEntry("notifications", userKey),
      setNotifications: (userKey, data) => setEntry("notifications", userKey, data),
      ttlMs: SHELL_CACHE_TTL_MS,
    }),
    [cache],
  );

  return <ShellCacheContext.Provider value={value}>{children}</ShellCacheContext.Provider>;
};

export const useShellCache = () => {
  const value = useContext(ShellCacheContext);
  if (!value) {
    return {
      getSidebarMeta: () => null,
      setSidebarMeta: () => {},
      getNotifications: () => null,
      setNotifications: () => {},
      ttlMs: SHELL_CACHE_TTL_MS,
    };
  }
  return value;
};
